import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    TextField,
    Chip,
    Button,
    IconButton,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    CircularProgress,
    Snackbar,
    Alert,
    MenuItem,
} from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread'
import DraftsIcon from '@mui/icons-material/Drafts'
import { DataGrid } from '@mui/x-data-grid'

import { mainApi } from '../../store/services/mainApi' // <-- поправь путь

// -------------------- endpoints --------------------
const GMAIL_ORDERS_URL = 'https://tpomobi.shop/getAllGmailOrders'
const ORDER_ITEMS_URL = 'https://tpomobi.shop/parsePrezo'

const SAVE_ORDER_ITEMS_URL = (orderNumber) =>
    `https://tpomobi.shop/saveOrderItemsByOrderNumber?orderNumber=${encodeURIComponent(orderNumber)}`

// -------------------- helpers --------------------
const safeStr = (v) => (v === null || v === undefined ? '' : String(v))

const formatDate = (date, isNight) => {
    const d = new Date(date)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd} ${isNight ? 'PLT 🌛' : 'PLM ☀️'}`
}

const getDeliveryDate = (timeFlag) => {
    const now = new Date()
    return now.getUTCHours() < 7
        ? formatDate(now, timeFlag)
        : formatDate(new Date(now.setDate(now.getDate() + 1)), timeFlag)
}

const toneChipSx = {
    borderRadius: 2,
    fontWeight: 700,
    letterSpacing: 0.2,
}

const pageSx = {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f6f7fb 0%, #ffffff 60%)',
    px: { xs: 2, md: 3 },
    py: { xs: 2, md: 3 },
}

const cardSx = {
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
}

// --- нормализация ответа parsePrezo под НОВЫЙ формат items (sku/name/format/qty) ---
const normalizeParsePrezoResponseToItems = (payload) => {
    const rawItems = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : []

    return rawItems.map((it, idx) => {
        const sku = safeStr(it?.sku ?? it?.ref ?? it?.Ref ?? it?.id ?? it?._id ?? `no-sku-${idx}`)
        const name = safeStr(it?.name ?? it?.description ?? it?.Descripción ?? it?.descripcion ?? '')
        const format = safeStr(it?.format ?? it?.Formato ?? it?.unit ?? it?.currentUnit ?? '')
        const qty = Number(it?.qty ?? it?.quantity ?? it?.quantityOrder ?? it?.Cantidad ?? 0) || 0

        return {
            _id: it?._id ?? `prezo-${sku}-${idx}`,
            sku,
            name,
            format,
            qty,

            // оставляем доп поля (не мешают, пригодятся для сохранения/редактирования)
            ourPriceCP: Number(it?.ourPriceCP ?? 0) || 0,
            marketPriceCP: Number(it?.marketPriceCP ?? 0) || 0,
            enabled: it?.enabled !== undefined ? !!it.enabled : true,
            proveedor: it?.proveedor ?? it?.supplier ?? it?.proveedorName ?? '',
            IVA: Number(it?.IVACP ?? it?.IVA ?? 0) || 0,

            __raw: it,
        }
    })
}

// -------------------- component --------------------
const PrezoPage = ({ selectedContact, deliveryType, time }) => {
    const dispatch = useDispatch()

    const [createOrder, { isLoading: isCreating }] = mainApi.useCreateNewOrderMutation()

    const [orders, setOrders] = useState([])
    const [loadingOrders, setLoadingOrders] = useState(false)
    const [ordersErr, setOrdersErr] = useState('')

    const [query, setQuery] = useState('')
    const [platformFilter, setPlatformFilter] = useState('all')

    // modal state
    const [activeOrder, setActiveOrder] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)

    const [itemsLoading, setItemsLoading] = useState(false)
    const [itemsErr, setItemsErr] = useState('')
    const [items, setItems] = useState([])

    // NEW: данные заказа, которые приходят с /parsePrezo (order.id/date/supplier/customer + debug)
    const [parsedMeta, setParsedMeta] = useState(null)

    const [dirty, setDirty] = useState(false)
    const [saving, setSaving] = useState(false)

    const [toast, setToast] = useState({
        open: false,
        severity: 'success',
        msg: '',
    })

    const showToast = (severity, msg) => setToast({ open: true, severity, msg })
    const closeToast = () => setToast((t) => ({ ...t, open: false }))

    const loadOrders = useCallback(async () => {
        setLoadingOrders(true)
        setOrdersErr('')
        try {
            const res = await fetch(GMAIL_ORDERS_URL)
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const data = await res.json()
            setOrders(Array.isArray(data) ? data : [])
        } catch (e) {
            setOrdersErr('Не удалось загрузить список заказов. Проверь endpoint и CORS.')
            setOrders([])
        } finally {
            setLoadingOrders(false)
        }
    }, [])

    useEffect(() => {
        loadOrders()
    }, [loadOrders])

    const platformOptions = useMemo(() => {
        const set = new Set(orders.map((o) => safeStr(o?.platform).toLowerCase()).filter(Boolean))
        return ['all', ...Array.from(set).sort()]
    }, [orders])

    const stats = useMemo(() => {
        const total = orders.length
        const unread = orders.filter((o) => o?.gmail?.isUnread).length
        const byPlatform = orders.reduce((acc, o) => {
            const p = safeStr(o?.platform).toLowerCase() || 'unknown'
            acc[p] = (acc[p] || 0) + 1
            return acc
        }, {})
        return { total, unread, byPlatform }
    }, [orders])

    const filteredOrders = useMemo(() => {
        const q = query.trim().toLowerCase()
        return orders.filter((o) => {
            const p = safeStr(o?.platform).toLowerCase()
            const platformOk = platformFilter === 'all' ? true : p === platformFilter
            if (!platformOk) return false
            if (!q) return true
            const hay = [o?.tradeName, o?.deliveryAddress, o?.orderNumber, o?.link, o?.gmail?.subject]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
            return hay.includes(q)
        })
    }, [orders, query, platformFilter])

    // -------------------- create order (твоя логика) --------------------
    const createNewOrderInChangeOrder = async (itemsArg, selectedMatches) => {
        if (!selectedContact?.value) {
            alert('Please select a contact')
            return
        }

        // itemsArg уже в нужном формате (sku/name/format/qty + доп поля)
        const body = {
            data: {
                img: 'https://imgur.com/mpuLhYe.jpeg',
                items: itemsArg ? itemsArg : [],
                date: Date.now(),
                type: 'browser',
                deliveryTime: time ? 'PLT 🌛' : 'PLM ☀️',
                deliveryDate: getDeliveryDate(time),
                status: {},
                order: 'CP',
                paidValue: 0,
                orderDeliveryType: deliveryType,
                userAcc: {
                    ...selectedContact.value,
                },
                tradeName: selectedContact.value?.tradeName ? selectedContact.value?.tradeName : '',
                selectedMatchesLast: selectedMatches,
            },
        }

        try {
            await createOrder(body)
            dispatch(mainApi.util.invalidateTags(['ShipmentOrders']))
            showToast('success', 'Заказ создан и теги ShipmentOrders инвалидированы.')
        } catch (err) {
            console.error(err)
            showToast('error', 'Ошибка создания заказа. См. консоль.')
        }
    }

    const onCreateFromRow = async (order) => {
        await createNewOrderInChangeOrder([], {
            source: 'gmail',
            platform: order?.platform,
            orderNumber: order?.orderNumber,
            tradeName: order?.tradeName,
        })
    }

    const onCreateFromModal = async () => {
        if (!activeOrder) return
        await createNewOrderInChangeOrder(items, {
            source: 'gmail',
            platform: activeOrder?.platform,
            orderNumber: activeOrder?.orderNumber,
            tradeName: activeOrder?.tradeName,
            // полезно добавить parsed order id, если есть
            parsedOrderId: parsedMeta?.order?.id || null,
        })
    }

    // -------------------- modal: load items --------------------
    const openOrderModal = async (order) => {
        setActiveOrder(order)
        setModalOpen(true)
        setItems([])
        setParsedMeta(null)
        setDirty(false)
        setItemsErr('')
        setItemsLoading(true)

        try {
            const res = await fetch(ORDER_ITEMS_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ link: order.link }),
            })
            if (!res.ok) throw new Error(`HTTP ${res.status}`)

            const data = await res.json()

            // ✅ сохраняем мета-инфу из ответа (order/date/supplier/customer/debug)
            setParsedMeta(data)

            // ✅ items в грид — только items[]
            const normalizedItems = normalizeParsePrezoResponseToItems(data)
            setItems(normalizedItems)
        } catch (e) {
            setItemsErr('Не удалось загрузить товары. Проверь ORDER_ITEMS_URL (endpoint/параметры).')
            setItems([])
            setParsedMeta(null)
        } finally {
            setItemsLoading(false)
        }
    }

    const closeModal = () => {
        setModalOpen(false)
        setActiveOrder(null)
        setItems([])
        setParsedMeta(null)
        setItemsErr('')
        setDirty(false)
    }

    // -------------------- save items --------------------
    const onSaveItems = async () => {
        if (!activeOrder?.orderNumber) return
        setSaving(true)
        try {
            const res = await fetch(SAVE_ORDER_ITEMS_URL(activeOrder.orderNumber), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items }),
            })
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            setDirty(false)
            showToast('success', 'Товары сохранены.')
        } catch (e) {
            showToast('error', 'Ошибка сохранения. Проверь SAVE_ORDER_ITEMS_URL и формат body.')
        } finally {
            setSaving(false)
        }
    }

    // -------------------- Orders table columns --------------------
    const ordersColumns = useMemo(
        () => [
            {
                field: 'platform',
                headerName: 'Platform',
                width: 130,
                sortable: true,
                renderCell: (params) => {
                    const unread = !!params.row?.gmail?.isUnread
                    return (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                                size="small"
                                label={safeStr(params.value || '—').toUpperCase()}
                                sx={{ ...toneChipSx }}
                                variant="outlined"
                            />
                            <Chip
                                size="small"
                                icon={unread ? <MarkEmailUnreadIcon /> : <DraftsIcon />}
                                label={unread ? 'unread' : 'read'}
                                color={unread ? 'warning' : 'success'}
                                sx={{ ...toneChipSx }}
                                variant="filled"
                            />
                        </Stack>
                    )
                },
            },
            {
                field: 'tradeName',
                headerName: 'TradeName',
                width: 200,
                sortable: true,
                renderCell: (params) => (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }} noWrap>
                            {safeStr(params.value || '—')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                            #{safeStr(params.row?.orderNumber || '—')} · {safeStr(params.row?.brand || '—')}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'deliveryAddress',
                headerName: 'Address',
                flex: 1,
                minWidth: 320,
                sortable: false,
                renderCell: (params) => (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                            {safeStr(params.value || '—')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                            {safeStr(params.row?.gmail?.subject || '')}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'date',
                headerName: 'Date',
                width: 200,
                sortable: true,
                valueGetter: (_, row) => row?.gmail?.date || '',
                valueFormatter: (params) => {
                    const raw = params.value
                    if (!raw) return '—'
                    const d = new Date(raw)
                    if (Number.isNaN(d.getTime())) return String(raw)
                    return d.toISOString().slice(0, 10)
                },
                renderCell: (params) => (
                    <Box>
                        <Typography variant="body2">{params.row?.gmail?.date || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {String(params.value || '')}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'gmailDate',
                headerName: 'Time',
                width: 220,
                valueGetter: (_, row) => row?.gmail?.date || '',
                sortable: false,
                renderCell: (params) => (
                    <Box>
                        <Typography variant="body2">{params.row?.deliveryTime || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {String(params.value || '')}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 210,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const link = params.row?.link
                    return (
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="Открыть ссылку в новом окне">
                <span>
                  <IconButton
                      size="small"
                      onClick={(e) => {
                          e.stopPropagation()
                          if (link) window.open(link, '_blank')
                      }}
                      disabled={!link}
                  >
                    <LaunchIcon fontSize="small" />
                  </IconButton>
                </span>
                            </Tooltip>

                            <Tooltip title="Создать новый заказ (CP)">
                <span>
                  <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                          e.stopPropagation()
                          onCreateFromRow(params.row)
                      }}
                      disabled={isCreating}
                      startIcon={
                          isCreating ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon />
                      }
                      sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 800,
                          boxShadow: 'none',
                      }}
                  >
                    Create
                  </Button>
                </span>
                            </Tooltip>
                        </Stack>
                    )
                },
            },
        ],
        [isCreating],
    )

    const ordersRows = useMemo(
        () =>
            filteredOrders.map((o, idx) => ({
                id: o?.gmail?.id || `${o?.orderNumber || 'ord'}-${idx}`,
                ...o,
            })),
        [filteredOrders],
    )

    // -------------------- Items grid (НУЖНЫЕ ПОЛЯ: sku / name / format / qty) --------------------
    const itemColumns = useMemo(
        () => [
            { field: '_id', headerName: '_id', width: 160 },
            { field: 'sku', headerName: 'sku', width: 180, editable: true },
            { field: 'name', headerName: 'name', flex: 1, minWidth: 320, editable: true },
            { field: 'format', headerName: 'format', width: 220, editable: true },
            { field: 'qty', headerName: 'qty', width: 110, type: 'number', editable: true },

            // оставляем остальное как было (не ломаем функционал сохранения/редактирования)
            { field: 'ourPriceCP', headerName: 'ourPriceCP', width: 140, type: 'number', editable: true },
            { field: 'marketPriceCP', headerName: 'marketPriceCP', width: 160, type: 'number', editable: true },
            { field: 'enabled', headerName: 'enabled', width: 110, type: 'boolean', editable: true },
            { field: 'proveedor', headerName: 'proveedor', width: 150, editable: true },
            { field: 'IVA', headerName: 'IVA', width: 90, type: 'number', editable: true },
        ],
        [],
    )

    const itemsRows = useMemo(
        () =>
            items.map((it, idx) => ({
                id: it?._id || it?.sku || `item-${idx}`,
                ...it,
            })),
        [items],
    )

    const processRowUpdate = (newRow, oldRow) => {
        setDirty(true)
        setItems((prev) => {
            const keyOld = oldRow?._id || oldRow?.sku
            const idx = prev.findIndex((x) => (x?._id || x?.sku) === keyOld)
            if (idx === -1) return prev
            const next = [...prev]
            const patched = { ...next[idx], ...newRow }

            // ✅ фикс типов под новый формат
            if (patched.qty !== undefined) patched.qty = Number(patched.qty) || 0
            if (patched.IVA !== undefined) patched.IVA = Number(patched.IVA) || 0
            if (patched.ourPriceCP !== undefined) patched.ourPriceCP = Number(patched.ourPriceCP) || 0
            if (patched.marketPriceCP !== undefined) patched.marketPriceCP = Number(patched.marketPriceCP) || 0

            next[idx] = patched
            return next
        })
        return newRow
    }

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                {/* Header */}
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'flex-end' }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.2 }}>
                            Gmail Orders — Prezo / Platforms
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Список: ссылка, tradeName, адрес, время. Клик по строке — модальное окно с загрузкой товаров,
                            редактированием и сохранением.
                        </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                            size="small"
                            label="Поиск"
                            placeholder="tradeName, адрес, orderNumber, тема…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            sx={{ minWidth: { xs: '100%', md: 360 } }}
                        />
                        <TextField
                            size="small"
                            select
                            label="Платформа"
                            value={platformFilter}
                            onChange={(e) => setPlatformFilter(e.target.value)}
                            sx={{ minWidth: { xs: '100%', md: 180 } }}
                        >
                            {platformOptions.map((p) => (
                                <MenuItem key={p} value={p}>
                                    {p === 'all' ? 'Все' : p}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Tooltip title="Обновить список">
              <span>
                <IconButton onClick={loadOrders} disabled={loadingOrders}>
                  <RefreshIcon />
                </IconButton>
              </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Stats */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <Card sx={{ ...cardSx, flex: 1 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="overline" color="text.secondary">
                                    Всего заказов
                                </Typography>
                                <Chip label={stats.total} color="info" size="small" sx={toneChipSx} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                В выборке: <b>{filteredOrders.length}</b>
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card sx={{ ...cardSx, flex: 1 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="overline" color="text.secondary">
                                    Unread (Gmail)
                                </Typography>
                                <Chip
                                    label={stats.unread}
                                    color={stats.unread ? 'warning' : 'success'}
                                    size="small"
                                    sx={toneChipSx}
                                />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Поле: <code>gmail.isUnread</code>
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card sx={{ ...cardSx, flex: 1 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="overline" color="text.secondary">
                                    Платформы
                                </Typography>
                                <Chip label={Object.keys(stats.byPlatform).length} size="small" sx={toneChipSx} />
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                                {Object.entries(stats.byPlatform)
                                    .sort((a, b) => b[1] - a[1])
                                    .slice(0, 8)
                                    .map(([k, v]) => (
                                        <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" sx={{ ...toneChipSx, mb: 1 }} />
                                    ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>

                {/* Orders grid */}
                <Card sx={cardSx}>
                    {loadingOrders ? <LinearProgress /> : null}

                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                Заказы
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {ordersErr ? 'Ошибка загрузки' : `Показано: ${ordersRows.length}`}
                            </Typography>
                        </Stack>

                        {ordersErr ? (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {ordersErr}
                            </Alert>
                        ) : null}

                        <Box sx={{ height: 620, width: '100%' }}>
                            <DataGrid
                                rows={ordersRows}
                                columns={ordersColumns}
                                disableRowSelectionOnClick
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                                onRowClick={(params) => openOrderModal(params.row)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2.5,
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: 'rgba(2, 6, 23, 0.02)',
                                        fontWeight: 900,
                                    },
                                    '& .MuiDataGrid-row': { cursor: 'pointer' },
                                }}
                            />
                        </Box>
                    </Box>
                </Card>

                {/* Modal with items */}
                <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="xl">
                    <DialogTitle sx={{ pr: 7 }}>
                        <Stack spacing={0.5}>
                            <Typography sx={{ fontWeight: 900 }}>
                                {activeOrder ? `Order #${safeStr(activeOrder.orderNumber)} · ${safeStr(activeOrder.tradeName)}` : 'Order'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Внутри: лоадер запроса товаров, редактирование и сохранение
                            </Typography>
                        </Stack>

                        <IconButton onClick={closeModal} sx={{ position: 'absolute', right: 12, top: 12 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ backgroundColor: '#fbfcff' }}>
                        {/* ✅ ОБНОВЛЕНО: модалка показывает данные из ответа /parsePrezo (order + debug) */}
                        {parsedMeta?.order ? (
                            <Stack spacing={2} sx={{ mb: 2 }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <Card sx={{ ...cardSx, flex: 1 }}>
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">
                                                TradeName
                                            </Typography>
                                            <Typography sx={{ fontWeight: 900 }}>
                                                {safeStr(activeOrder.tradeName || '—')}
                                            </Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                <Chip
                                                    label={safeStr(activeOrder.platform || '—')}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={toneChipSx}
                                                />
                                                <Chip
                                                    label={`Brand: ${safeStr(activeOrder.brand || '—')}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={toneChipSx}
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>

                                    <Card sx={{ ...cardSx, flex: 2 }}>
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">
                                                Address
                                            </Typography>
                                            <Typography sx={{ fontWeight: 700 }}>
                                                {safeStr(activeOrder.deliveryAddress || '—')}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ mt: 1, display: 'block' }}
                                            >
                                                {safeStr(activeOrder.gmail?.subject || '')}
                                            </Typography>
                                        </CardContent>
                                    </Card>

                                    <Card sx={{ ...cardSx, flex: 1 }}>
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">
                                                Gmail
                                            </Typography>
                                            <Typography sx={{ fontWeight: 700 }}>
                                                {safeStr(activeOrder.gmail?.from || '—')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {safeStr(activeOrder.gmail?.date || '')}
                                            </Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                <Chip
                                                    label={activeOrder.gmail?.isUnread ? 'unread' : 'read'}
                                                    size="small"
                                                    color={activeOrder.gmail?.isUnread ? 'warning' : 'success'}
                                                    sx={toneChipSx}
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Stack>

                                <Divider />
                            </Stack>

                        ) :  activeOrder ? (
                                <Stack spacing={2} sx={{ mb: 2 }}>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                        <Card sx={{ ...cardSx, flex: 1 }}>
                                            <CardContent>
                                                <Typography variant="overline" color="text.secondary">
                                                    TradeName
                                                </Typography>
                                                <Typography sx={{ fontWeight: 900 }}>
                                                    {safeStr(activeOrder.tradeName || '—')}
                                                </Typography>
                                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                    <Chip
                                                        label={safeStr(activeOrder.platform || '—')}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={toneChipSx}
                                                    />
                                                    <Chip
                                                        label={`Brand: ${safeStr(activeOrder.brand || '—')}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={toneChipSx}
                                                    />
                                                </Stack>
                                            </CardContent>
                                        </Card>

                                        <Card sx={{ ...cardSx, flex: 2 }}>
                                            <CardContent>
                                                <Typography variant="overline" color="text.secondary">
                                                    Address
                                                </Typography>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    {safeStr(activeOrder.deliveryAddress || '—')}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ mt: 1, display: 'block' }}
                                                >
                                                    {safeStr(activeOrder.gmail?.subject || '')}
                                                </Typography>
                                            </CardContent>
                                        </Card>

                                        <Card sx={{ ...cardSx, flex: 1 }}>
                                            <CardContent>
                                                <Typography variant="overline" color="text.secondary">
                                                    Gmail
                                                </Typography>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    {safeStr(activeOrder.gmail?.from || '—')}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {safeStr(activeOrder.gmail?.date || '')}
                                                </Typography>
                                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                    <Chip
                                                        label={activeOrder.gmail?.isUnread ? 'unread' : 'read'}
                                                        size="small"
                                                        color={activeOrder.gmail?.isUnread ? 'warning' : 'success'}
                                                        sx={toneChipSx}
                                                    />
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Stack>

                                    <Divider />
                                </Stack>
                            ) : null}

                        {/* items loader / error / grid */}
                        {itemsLoading ? (
                            <Card sx={{ ...cardSx }}>
                                <LinearProgress />
                                <CardContent>
                                    <Stack spacing={1.2}>
                                        <Typography sx={{ fontWeight: 900 }}>Загружаю товары…</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Идёт визуальная обработка запроса к серверу
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ) : itemsErr ? (
                            <Alert severity="error">{itemsErr}</Alert>
                        ) : (
                            <Card sx={{ ...cardSx }}>
                                <CardContent>
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1}
                                        alignItems={{ xs: 'stretch', md: 'center' }}
                                        justifyContent="space-between"
                                        sx={{ mb: 1.5 }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                                Товары
                                            </Typography>
                                            <Chip label={items.length} size="small" sx={toneChipSx} variant="outlined" />
                                            {dirty ? (
                                                <Chip label="есть изменения" color="warning" size="small" sx={toneChipSx} />
                                            ) : (
                                                <Chip label="всё сохранено" color="success" size="small" sx={toneChipSx} />
                                            )}
                                        </Stack>

                                        <Typography variant="caption" color="text.secondary">
                                            Двойной клик по ячейке — редактирование
                                        </Typography>
                                    </Stack>

                                    <Box sx={{ height: 520, width: '100%' }}>
                                        <DataGrid
                                            rows={itemsRows}
                                            columns={itemColumns}
                                            processRowUpdate={processRowUpdate}
                                            onProcessRowUpdateError={(err) => {
                                                console.error(err)
                                                showToast('error', 'Ошибка обновления строки.')
                                            }}
                                            disableRowSelectionOnClick
                                            pageSizeOptions={[25, 50, 100]}
                                            initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2.5,
                                                '& .MuiDataGrid-columnHeaders': {
                                                    backgroundColor: 'rgba(2, 6, 23, 0.02)',
                                                    fontWeight: 900,
                                                },
                                            }}
                                        />
                                    </Box>

                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                        Если нужно добавить/убрать колонки (barcode, skuProveedor, place/shelft/stand, цены DP/SDP, и т.п.)
                                        — скажи, сделаю компактно.
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1}
                            sx={{ width: '100%' }}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            justifyContent="space-between"
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={activeOrder?.link ? 'Ссылка доступна' : 'Нет ссылки'} size="small" variant="outlined" sx={toneChipSx} />
                            </Stack>

                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => activeOrder?.link && window.open(activeOrder.link, '_blank')}
                                    disabled={!activeOrder?.link}
                                    startIcon={<LaunchIcon />}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                                >
                                    Открыть оригинал
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={onSaveItems}
                                    disabled={saving || itemsLoading || !dirty}
                                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                                >
                                    Сохранить
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={onCreateFromModal}
                                    disabled={isCreating || itemsLoading}
                                    startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 900,
                                        boxShadow: 'none',
                                    }}
                                >
                                    Создать заказ из товаров
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={toast.open}
                    autoHideDuration={3200}
                    onClose={closeToast}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {toast.msg}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    )
}

export default PrezoPage
